import { Close } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import type {
  ProjectNotes,
  ProjectRunAnalysisQuestion,
  StepDependenciesOptions,
} from 'Types/dealGPT';

import { checkDependencies, getNotes, saveNotes } from '../../../services/dealGPT';
import LoadingMask from '../../LoadingMask';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import { useNotifications } from '../../Providers/NotificationsProvider';

const NotesContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

interface NotesProps {
  projectId: string;
  scope: string;
  latestRunId?: string;
  question?: ProjectRunAnalysisQuestion;
  refetchQuestions: (isFullScan: boolean) => void;
  firstRun: boolean;
  isParentLoading: boolean;
}

const Notes: React.FC<NotesProps> = ({
  projectId,
  scope,
  question,
  refetchQuestions,
  firstRun,
  isParentLoading,
}) => {
  const [projectNotes, setProjectNotes] = useState<ProjectNotes>();
  const [notesQuestion, setNotesQuestion] = useState<ProjectRunAnalysisQuestion | undefined>(
    question
  );
  const [dependencyMap, setDependencyMap] = useState<Map<string, string[]>>();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const analytics = useAnalytics();
  const MAX_NOTES_LENGTH = 230000;
  const { refetchNotifications } = useNotifications();

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        setIsLoading(true);
        const notes = await getNotes(projectId, scope);
        if (notes?.id) {
          setProjectNotes(notes);
        } else {
          setProjectNotes({
            projectId: projectId,
            notes: '',
            scope: scope,
          });
        }
      } catch {
        enqueueSnackbar('Failed to fetch notes. Please try again later.', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotes();
  }, [projectId, scope]);

  useEffect(() => {
    if (question) {
      setNotesQuestion(question);
    }
  }, [question]);

  const handleCloseDialog = (sendAnalyticEvent = true) => {
    if (sendAnalyticEvent) {
      analytics.event(
        AnalyticsEvent.WM_IA_A_SITE_NOTES_DIALOG_CLOSE,
        {
          id: projectId,
          value: scope,
        },
        {
          projectId: projectId,
          scope: scope,
        }
      );
    }
    setOpenDialog(false);
  };

  const onSubmit = async (values: ProjectNotes) => {
    // validation?
    setIsLoading(true);
    handleCloseDialog(false);
    analytics.event(
      AnalyticsEvent.WM_IA_A_SITE_NOTES_DIALOG_CONTINUE,
      {
        id: projectId,
        value: scope,
      },
      {
        projectId: projectId,
        scope: scope,
      }
    );
    try {
      await saveNotes(values, true, notesQuestion?.runId);
      refetchNotifications();
      refetchQuestions(true);
      setIsLoading(false);
      enqueueSnackbar('Notes saved successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error processing notes. Please try again.', { variant: 'error' });
    }
  };

  const handleSave = async (values: ProjectNotes) => {
    // Save notes to the database
    analytics.event(
      AnalyticsEvent.WM_IA_A_SITE_NOTES_SAVE,
      {
        id: projectId,
        value: scope,
      },
      {
        projectId: projectId,
        scope: scope,
      }
    );
    try {
      await saveNotes(values, false, notesQuestion?.runId);
      refetchNotifications();
      refetchQuestions(true);
      setIsLoading(false);
      enqueueSnackbar('Notes saved successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error processing notes. Please try again.', { variant: 'error' });
    }
  };

  const handleOpenDialog = async () => {
    analytics.event(
      AnalyticsEvent.WM_IA_A_SITE_NOTES_PROCESS_PROJECT,
      {
        id: projectId,
        value: scope,
      },
      {
        projectId: projectId,
        scope: scope,
      }
    );
    let dependencies: StepDependenciesOptions | undefined = undefined;

    if (notesQuestion) {
      dependencies = await checkDependencies(notesQuestion);
    }

    if (dependencies) {
      const uniqueDependencyMap: Map<string, Set<string>> = new Map();
      dependencies.dependencies.forEach((dependency) => {
        const key = dependency.pageName;
        if (!uniqueDependencyMap.has(key)) {
          uniqueDependencyMap.set(key, new Set());
        }

        const promptTypes = uniqueDependencyMap.get(key);
        if (promptTypes && dependency.promptType) {
          promptTypes.add(dependency.promptType);
        }
      });

      const result = new Map();
      uniqueDependencyMap.forEach((value, key) => {
        result.set(key, Array.from(value));
      });

      setDependencyMap(result);
    }

    setOpenDialog(true);
  };

  const validate = useCallback((values: ProjectNotes) => {
    const errors: Partial<ProjectNotes> = {};
    if (values.notes && values.notes.length > MAX_NOTES_LENGTH) {
      errors.notes = `Notes cannot exceed ${MAX_NOTES_LENGTH.toLocaleString()} characters.`;
    }
    return errors;
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Form
        onSubmit={onSubmit}
        initialValues={projectNotes}
        validate={validate}
        render={({ handleSubmit, values, errors, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            <NotesContainer>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="h2" sx={{ fontSize: '24px' }}>
                    Notes
                  </Typography>
                </div>
              </div>
              {errors?.notes && (
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  {errors.notes}
                </Typography>
              )}
              <div style={{ display: 'flex', gap: '16px', width: '100%', justifyContent: 'end' }}>
                <Tooltip
                  title={
                    <div style={{ whiteSpace: 'pre-line' }}>
                      {
                        "Save what you have. Use this to keep working on these notes later.\n\nWhen you're ready, apply the changes to downstream dependencies so the LLM can update its responses with the right information."
                      }
                    </div>
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      onClick={() => handleSave(values)}
                      disabled={!notesQuestion || hasValidationErrors}
                    >
                      Save
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    <div style={{ whiteSpace: 'pre-line' }}>
                      {!firstRun
                        ? 'Have this how you want it? Save these changes and immediately "apply" them to downstream dependencies. This will regenerate LLM responses on all pages.\n\nDon\'t worry! You can switch between versions if you liked what you had, but give it a try!'
                        : 'Have this how you want it? This will generate LLM responses on all pages.'}
                    </div>
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleOpenDialog}
                      disabled={!notesQuestion || isLoading || hasValidationErrors}
                    >
                      {firstRun ? 'Process Project' : 'Save and Apply Changes'}
                    </Button>
                  </span>
                </Tooltip>
              </div>
              <Dialog
                open={openDialog}
                onClose={() => {
                  handleCloseDialog();
                }}
              >
                <DialogTitle>Are you sure you want to apply changes?</DialogTitle>
                <IconButton
                  onClick={() => {
                    handleCloseDialog();
                  }}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: '#757575',
                  }}
                >
                  <Close />
                </IconButton>
                <DialogContent style={{ paddingTop: '0px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" sx={{ color: 'red', paddingBottom: '16px' }}>
                      Please be aware this process should take 5-10 minutes.
                    </Typography>
                    <Typography>
                      This will generate or regenerate the following dependencies:
                    </Typography>
                    <Typography>
                      <ul>
                        {dependencyMap &&
                          Array.from(dependencyMap.entries()).map(([key, value], index) => (
                            <li key={`dependency-${index}`}>
                              <Typography>{`${
                                value.length > 0 ? value.join(', ') + ' on page ' : ''
                              }${key}`}</Typography>
                            </li>
                          ))}
                      </ul>
                    </Typography>
                  </div>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => onSubmit(values)}>Continue</Button>
                </DialogActions>
              </Dialog>
              <div style={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
                <Typography>
                  Please enter any notes relevant to analysis you want Intellio® Advantage to
                  prioritize for answers and content generation
                </Typography>
              </div>
              <Field name="notes" subscription={{ value: true, error: true }}>
                {({ input, meta }) => {
                  return <TextField {...input} error={meta.error} multiline variant="outlined" />;
                }}
              </Field>
              {errors?.notes && (
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  {errors.notes}
                </Typography>
              )}
              <LoadingMask isLoading={isLoading && !isParentLoading} />
            </NotesContainer>
          </form>
        )}
      />
    </div>
  );
};

export default Notes;
